import React, { useState, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, IconButton, TextField, Box, Chip, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/DateUtils';
import { getIcon } from '../../../utils/IconHandler';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import B2BUtilsService from '../../../services/B2BUtilsService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import EditPaymentStatusModal from '../billing_statment/EditPaymentStatusModal';
import { formatMoney } from '../../../utils/currency';

const B2BOrdersTable = ({ 
  orders = [], 
  pagination = { totalCount: 0, currentPage: 1, totalPages: 1, pageSize: 10 },
  searchTerm = '',
  sortModel = [],
  onSortModelChange,
  filterModel = { items: [] },
  onFilterModelChange,
  onAdd, 
  onEdit, 
  onView, 
  onDelete, 
  onEditPaymentStatus, 
  onEditStatus, 
  onViewInvoice, 
  onPaginationChange,
  onSearchChange,
  onRefresh,
  showAddButton = true,
  readOnlyActions = false,
  loading, 
  loadingOrderId 
}) => {
  const { t } = useTranslation();
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  
  // Convertir paginación del backend (base 1) a DataGrid (base 0)
  const paginationModel = {
    page: pagination.currentPage - 1,
    pageSize: pagination.pageSize
  };
  
  dayjs.extend(relativeTime);
  dayjs.locale('es');

  // Asegurar que orders sea siempre un array
  const safeOrders = Array.isArray(orders) ? orders : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'FINALIZED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };


  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING': return t('pending');
      case 'FINALIZED': return 'Entregado';
      case 'CANCELLED': return t('cancel');
      default: return status;
    }
  };

  const handleEditPaymentStatus = (order) => {
    setSelectedOrder(order);
    setIsPaymentModalOpen(true);
  };

  const handleUpdatePaymentStatus = async (updatedPaymentData) => {
    try {
      if (onEditPaymentStatus) {
        await onEditPaymentStatus(selectedOrder, updatedPaymentData);
      }
      setIsPaymentModalOpen(false);
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: "ID", 
      flex: .5 
    },
    { 
      field: 'clientName', 
      headerName: t('client'), 
      flex: 1.2
    },
    {
      field: 'paymentStatus',
      headerName: t('payment_status'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const dueDate = params.row.dueDate || B2BUtilsService.calculateDueDate(params.row.orderDate, params.row.creditDays);
        return (
          <Chip
            label={B2BUtilsService.getBillingOrderStatusText(params.value, dueDate, t)}
            color={B2BUtilsService.getBillingOrderStatusColor(params.value, dueDate)}
            size="small"
          />
        );
      }
    },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={getStatusText(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'orderDate',
      headerName: t('order_date'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = dayjs(params.value);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>{formatDate(date)}</div>
          </Box>
        );
      }
    },
    { 
      field: 'totalAmount', 
      headerName: t('total_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const totalAmount = params.row.totalAmount || 0;
        return (
          <Box sx={{ textAlign: 'center' }}>
              {formatMoney(totalAmount)}
          </Box>
        );
      }
    },
    { 
      field: 'paidAmount', 
      headerName: t('paid_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        const paidAmount = params.row.paidAmount || 0;
        const pendingAmount = params.row.pendingAmount || 0;        
        return (
          <Box sx={{ 
            textAlign: 'center', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: paidAmount > 0 ? 'success.main' : 'text.secondary' }}>
              {formatMoney(paidAmount)}
            </Typography>
            {pendingAmount > 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {t('pending')}: {formatMoney(pendingAmount)}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: readOnlyActions ? 0.6 : 1.7,
      minWidth: readOnlyActions ? 96 : 190,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const isThisOrderLoading = loadingOrderId === params.row.id;
        return (
          <>
            <IconButton onClick={() => onView(params.row)}>{getIcon('eye')}</IconButton>
            <IconButton 
              onClick={() => onViewInvoice(params.row)}
              disabled={isThisOrderLoading}
            >
              {isThisOrderLoading ? (
                <CircularProgress size={20} />
              ) : (
                getIcon('reader')
              )}
            </IconButton>
            {!readOnlyActions && params.row.status !== 'FINALIZED' && params.row.status !== 'CANCELLED' && (
              <IconButton onClick={() => onEditStatus(params.row)}>{getIcon('settings')}</IconButton>
            )}
            {!readOnlyActions && params.row.paymentStatus !== 'PAID' && (
              <IconButton onClick={() => handleEditPaymentStatus(params.row)}>{getIcon('money')}</IconButton>
            )}
            {!readOnlyActions && (
              <IconButton onClick={() => onDelete(params.row)}>{getIcon('trash')}</IconButton>
            )}
          </>
        );
      }
    }
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          sx={{ width: 300 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <TableRefreshButton loading={loading} onClick={onRefresh} />
          {showAddButton && (
            <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
              {t('add_b2b_order')}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: readOnlyActions ? 860 : 1100 }}>
          <DataGrid
            loading={loading}
          rows={safeOrders}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          sortingMode="server"
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          filterMode="server"
          pageSizeOptions={[10, 25, 50]}
          paginationMode="server"
            rowCount={pagination.totalCount}
            getRowId={(row) => row.id || row.orderNumber}
            autoHeight
          />
        </Box>
      </Box>
      
      <EditPaymentStatusModal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSave={handleUpdatePaymentStatus}
        statement={selectedOrder ? {
          id: selectedOrder.id,
          statementNumber: selectedOrder.orderNumber,
          clientName: selectedOrder.clientName,
          totalAmount: selectedOrder.totalAmount,
          paidAmount: selectedOrder.paidAmount || 0,
          pendingAmount: selectedOrder.totalAmount - (selectedOrder.paidAmount || 0),
          status: selectedOrder.paymentStatus
        } : null}
      />
    </Paper>
  );
};

export default B2BOrdersTable;
